import { useEffect } from 'react';
import { connectToSocket, socket } from '../api/socket';
import { queryClient } from '../queryClient';
import { useGetUserFromAccessToken } from '../common/auth';

enum UserSocketEvent {
  notificationsChanged = 'notificationsChanged',
}

export const useUserOrganizationSocket = (organizationId: number) => {
  const userId = useGetUserFromAccessToken().data?.user?.id;

  useEffect(() => {
    if (!userId) return;
    let aborted = false;

    const handleNotifications = () => {
      void queryClient.invalidateQueries({
        queryKey: ['notifications', organizationId],
        refetchType: 'active',
      });
      void queryClient.invalidateQueries({
        queryKey: ['inbox', organizationId],
        refetchType: 'active',
      });
    };

    // The badge query is cached for 24h, so the socket is the only thing that keeps it
    // current. Any notificationsChanged sent while nothing was listening is gone for good:
    // a dropped connection (sleep, network change, server restart), or a window with no
    // host mounted. Refetch whenever we (re)subscribe so a missed event cannot leave the
    // count stale until a page refresh (DEV-3223).
    const subscribe = () => {
      socket.emit('register', userId);
      handleNotifications();
    };

    // Re-register whenever the socket reconnects so the server always knows this client.
    const handleConnect = () => subscribe();

    void connectToSocket().then(() => {
      if (aborted) return;
      subscribe();
      socket.on('connect', handleConnect);
      socket.on(UserSocketEvent.notificationsChanged, handleNotifications);
    });

    return () => {
      aborted = true;
      socket.off('connect', handleConnect);
      socket.off(UserSocketEvent.notificationsChanged, handleNotifications);
    };
  }, [userId, organizationId]);
};
